import { motion } from "framer-motion";
import { Award, HeartHandshake, Landmark } from "lucide-react";

const STATS = [
  { icon: Award, value: "20+ yrs", label: "Award-winning REALTOR®" },
  { icon: HeartHandshake, value: "98%", label: "Referral & repeat business" },
  { icon: Landmark, value: "Past President", label: "Local Real Estate Board" },
];

export default function AboutJoselle() {
  return (
    <section id="about" data-testid="about-section" className="relative pt-10 pb-28 sm:pt-14 sm:pb-36 bg-[#0D0B0E] overflow-hidden">
      <div className="absolute -left-40 top-1/3 w-[30rem] h-[30rem] rounded-full bg-[#E890A7]/8 blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1 }}
          className="relative mx-auto w-full max-w-md"
        >
          <div className="absolute -inset-8 bg-[radial-gradient(circle_at_50%_30%,rgba(232,144,167,0.28),transparent_65%)] blur-2xl pointer-events-none" />
          <div className="relative rounded-t-[12rem] rounded-b-[2rem] overflow-hidden border border-[#E890A7]/25 rose-glow">
            <img
              src="/assets/joselle-portrait.jpg"
              alt="Joselle Alves, Broker"
              data-testid="about-portrait"
              className="w-full h-[34rem] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B0E]/60 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white/95 rounded-full px-5 py-2.5 shadow-2xl shadow-black/50">
            <img src="/assets/joselle-logo.png" alt="Joselle Alves signature" className="h-8 w-auto" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15 }}
        >
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#E890A7]/80 mb-4">
            02 · Your Lagoon City Real Estate Specialist
          </p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Joselle <em className="italic text-[#E890A7]">Alves</em>
          </h2>
          <p className="mt-8 text-slate-300/90 font-light leading-relaxed text-base sm:text-lg">
            With near three decades of award-winning real estate experience — and more than 20 years
            as a Lagoon City resident — Joselle offers something that goes far beyond expertise: a
            deep, personal connection to the community she proudly calls her forever home.
          </p>
          <p className="mt-5 text-slate-400 font-light leading-relaxed text-sm sm:text-base">
            Lagoon City isn&apos;t just where she works — it&apos;s where she lives. Living here
            full-time, she understands the waterfront lifestyle, the canals and the qualities that
            draw people here, and she has a genuine vested interest in seeing Lagoon City grow,
            thrive and hold strong property values for the homeowners who call it home.
          </p>
          <p className="mt-5 text-slate-400 font-light leading-relaxed text-sm sm:text-base">
            Her business is built on relationships, trust and results — roughly 98% of it comes from
            referrals and repeat clients. She has served as President of her local Real Estate Board
            and chaired its Government Relations Committee, travelling throughout North America to
            advocate on homeownership issues, and she mentors fellow REALTORS® on building
            professional, referral-based businesses of their own.
          </p>
          <p className="mt-7 font-display italic text-xl sm:text-2xl text-[#F2A7BA] leading-snug">
            &ldquo;Hiring the right REALTOR® always makes a difference. Time to buy or sell? Call Joselle.&rdquo;
          </p>

          <div className="mt-10 grid grid-cols-3 gap-4" data-testid="about-stats">
            {STATS.map((s) => (
              <div
                key={s.label}
                className="rounded-2xl border border-[#E890A7]/15 bg-[#151218]/70 p-4 hover:border-[#E890A7]/40 transition-colors duration-300"
              >
                <s.icon size={18} className="text-[#E890A7] mb-3" />
                <p className="font-display text-2xl sm:text-3xl text-slate-100">{s.value}</p>
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">{s.label}</p>
              </div>
            ))}
          </div>

          <button
            data-testid="about-contact-btn"
            onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
            className="mt-10 rounded-full border border-[#E890A7]/40 text-[#F2A7BA] px-7 py-3 text-sm hover:bg-[#E890A7]/10 hover:border-[#E890A7]/70 transition-all duration-300"
          >
            Meet Joselle for a consultation
          </button>
        </motion.div>
      </div>
    </section>
  );
}
